import React, { useState, useEffect } from 'react';
import { getForecastVersionHistory, restoreForecastVersion } from '../../utils/forecastApi';

const VersionHistoryDropdown = ({ orgId, locationId, chartOfAccountId, year, onRestore }) => {
  const [versions, setVersions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  // Fetch version history when dropdown opens
  useEffect(() => {
    if (isOpen && orgId && locationId && chartOfAccountId && year) {
      fetchVersionHistory();
    }
  }, [isOpen, orgId, locationId, chartOfAccountId, year]);

  const fetchVersionHistory = async () => {
    setIsLoading(true);
    try {
      const history = await getForecastVersionHistory({
        orgId,
        locationId,
        chartOfAccountId,
        year
      });
      setVersions(history || []);
    } catch (error) {
      console.error('Error fetching version history:', error);
      setVersions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async (version) => {
    if (!window.confirm(`Are you sure you want to restore version ${version.version} from ${formatDateTime(version.updatedAt)}?`)) {
      return;
    }

    setIsRestoring(true);
    try {
      await restoreForecastVersion(version);
      setIsOpen(false);

      // Notify parent component to refresh data
      if (onRestore) {
        onRestore();
      }
    } catch (error) {
      console.error('Error restoring version:', error);
      alert('Failed to restore version. Please try again.');
    } finally {
      setIsRestoring(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'Unknown';

    const date = new Date(isoString);
    // Format to user's local time
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  const currentVersion = versions.find(v => v.isCurrentVersion);

  return (
    <div className="relative inline-block text-left">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1"
        disabled={isRestoring}
      >
        Version History
        {currentVersion && (
          <span className="ml-2 text-gray-500">
            (v{currentVersion.version})
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown menu */}
          <div className="absolute right-0 z-20 mt-2 w-96 bg-white border border-gray-200 rounded-md shadow-lg">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900">Version History</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Restore a previous version or view change history
              </p>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                </div>
              ) : versions.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  No version history available
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {versions.map((version) => (
                    <li
                      key={version.id}
                      className={`px-4 py-3 hover:bg-gray-50 ${
                        version.isCurrentVersion ? 'bg-green-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold text-gray-900">
                              Version {version.version}
                            </span>
                            {version.isCurrentVersion && (
                              <span className="px-1.5 py-0.5 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded">
                                Current
                              </span>
                            )}
                          </div>

                          <div className="text-xs text-gray-600 space-y-0.5">
                            <div>
                              <span className="font-medium">Last edited by:</span>{' '}
                              <span className="text-gray-700">
                                {version.updatedBy || version.createdBy || 'Unknown'}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium">Last edited:</span>{' '}
                              <span className="text-gray-700">
                                {formatDateTime(version.updatedAt)}
                              </span>
                            </div>
                            {version.createdBy && version.createdAt && (
                              <div>
                                <span className="font-medium">Created by:</span>{' '}
                                <span className="text-gray-700">
                                  {version.createdBy}
                                </span>
                                {' on '}
                                <span className="text-gray-700">
                                  {formatDateTime(version.createdAt)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {!version.isCurrentVersion && (
                          <button
                            onClick={() => handleRestore(version)}
                            disabled={isRestoring}
                            className="ml-3 px-2 py-1 text-xs font-medium text-orange-600 bg-white border border-orange-300 rounded hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Restore
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VersionHistoryDropdown;
